// Tenantra Inc. - Paywall Modal Component
// Shows when a user tries to perform an action without an active subscription.
import { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/services/api';
import { toast } from 'sonner';

// Subscription plans
const SUBSCRIPTION_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    properties: '1-3 properties',
    color: 'blue',
    features: ['Unlimited reports', 'Unlimited lookups', 'Unlimited tenants']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 59,
    properties: '4-10 properties',
    color: 'purple',
    badge: 'Popular',
    features: ['Everything in Starter', 'Priority Support', 'Team Accounts']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 99,
    properties: 'Unlimited properties',
    color: 'teal',
    badge: 'Best Value',
    features: ['Everything in Professional', 'API Access', 'Bulk Operations']
  }
];

export default function PaywallModal({ isOpen, onClose, message }) {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await api.post('/billing/subscribe', {
        plan: planId,
        success_url: `${window.location.origin}${window.location.pathname}?payment=success`,
        cancel_url: `${window.location.origin}${window.location.pathname}?payment=canceled`
      });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="paywall-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Choose a plan</h2>
            <p className="text-sm text-slate-500 mt-0.5">{message || 'Subscribe to continue'}</p>
          </div>
          <button onClick={onClose} disabled={loading} className="text-slate-400 hover:text-slate-600" data-testid="paywall-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Plans */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <div key={plan.id} className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">{plan.name}</h4>
                    <p className="text-xs text-slate-500">{plan.properties}</p>
                  </div>
                  {plan.badge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${plan.color}-100 text-${plan.color}-700 font-medium`}>
                      {plan.badge}
                    </span>
                  )}
                </div>

                <div className="mb-3">
                  <span className="text-2xl font-bold text-slate-800">${plan.price}</span>
                  <span className="text-sm text-slate-500">/month</span>
                </div>

                <ul className="space-y-1.5 mb-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="text-xs text-slate-600 flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading}
                  className={`w-full h-9 bg-${plan.color}-600 hover:bg-${plan.color}-700 text-white`}
                  data-testid={`paywall-subscribe-${plan.id}`}
                >
                  {loading ? 'Processing...' : 'Subscribe'}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center">
            Secure payment powered by Stripe • All transactions are encrypted
          </p>
        </div>
      </div>
    </div>
  );
}
